import { navigate } from "gatsby";
import type React from "react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

const CHATBOT_SCRIPT_URL =
	"https://apps.fairtouch.siemens.com/webcomponents/chatbot/hm26/assets/scripts.js";

declare global {
	interface Window {
		b12_chatbot?: {
			isOpen: () => boolean;
			registerLinkHandler: (
				pattern: string,
				handler: (url: string) => void,
			) => void;
			unregisterLinkHandler: (pattern: string) => void;
			setLanguage: (lccc: string) => void;
			setLayout: (layout: "default" | "mobile") => void;
		};
	}
}

const getSitePattern = (): string => {
	const raw =
		process.env.GATSBY_SITE_URL_CHATBOT_TEST ??
		process.env.GATSBY_SITE_URL ??
		"";
	try {
		return new URL(raw.replace(/\/$/, "")).hostname;
	} catch {
		return "";
	}
};

const SITE_PATTERN = getSitePattern();

export const B12Chatbot: React.FC = () => {
	const { i18n } = useTranslation();

	useEffect(() => {
		const lccc = i18n.language.startsWith("de") ? "de-DE" : "en-US";
		const waitForChatbot = () => {
			if (window.b12_chatbot) {
				window.b12_chatbot.setLanguage(lccc);
			} else {
				setTimeout(waitForChatbot, 100);
			}
		};
		waitForChatbot();
	}, [i18n.language]);

	useEffect(() => {
		const updateLayout = () => {
			const layout = window.innerWidth < 600 ? "mobile" : "default";
			console.log("SET CHATBOT LAYOUT TO", layout);
			window.b12_chatbot?.setLayout(layout);
		};

		const waitForChatbot = () => {
			if (window.b12_chatbot) {
				updateLayout();
				window.addEventListener("resize", updateLayout);
			} else {
				setTimeout(waitForChatbot, 100);
			}
		};
		waitForChatbot();

		return () => {
			window.removeEventListener("resize", updateLayout);
		};
	}, []);

	useEffect(() => {
		if (!SITE_PATTERN) return;

		const waitForChatbot = () => {
			if (window.b12_chatbot) {
				window.b12_chatbot.registerLinkHandler(SITE_PATTERN, (url) => {
					const { pathname, search, hash } = new URL(url);
					navigate(pathname + search + hash);

					if (hash) {
						// Strip only the leading # to get the element ID (e.g. #/agenda -> /agenda)
						const id = hash.replace(/^#/, "");
						// Use a longer delay when navigating to a different page so the
						// new page has time to render before we attempt to scroll
						const delay = window.location.pathname === pathname ? 100 : 800;
						setTimeout(() => {
							const el = document.getElementById(id);
							if (el) {
								window.scrollTo({
									behavior: "smooth",
									top:
										el.getBoundingClientRect().top -
										document.body.getBoundingClientRect().top,
								});
							}
						}, delay);
					}
				});
			} else {
				setTimeout(waitForChatbot, 100);
			}
		};
		waitForChatbot();

		return () => {
			window.b12_chatbot?.unregisterLinkHandler(SITE_PATTERN);
		};
	}, []);

	return (
		<Helmet>
			<script type="module" src={CHATBOT_SCRIPT_URL} />
		</Helmet>
	);
};
